import type {
	AppConfig,
	AppSettingsValues,
	Cart,
	DeepPartial,
	NubeSDKState,
} from "@tiendanube/nube-sdk-types";
import type { AppUI, GlobalUI } from "./ui-extensions";

export type AppID = string;

export type AppError = { code: string; message?: string } | string;

export type AppState = {
	id: AppID;
	script: string;
	config?: AppConfig;
	registered?: boolean;
	cart?: Pick<Cart, "validation">;
	errors?: AppError[];
	ui?: AppUI;
	appSettings?: AppSettingsValues;
};

export type NubeSDKRuntimeState = Omit<NubeSDKState, "ui"> & {
	ui: GlobalUI;
} & DeepPartial<{
		apps: Record<AppID, AppState>;
	}>;

/**
 * The only `AppState` keys a worker is allowed to see. Everything else in
 * `AppState` is either main-thread machinery (`script`, `config`), another
 * app's private data (`ui`, `cart`, `errors`) or a secret that belongs to a
 * single app (`appSettings`) — an app reads its own settings through
 * `nube.getAppSettings()`, never through the state of its neighbours.
 *
 * Keep this as an allowlist, not a denylist: a key added to `AppState` later
 * must stay invisible to workers until someone deliberately lists it here.
 */
export const PUBLIC_APP_STATE_KEYS = ["id", "registered"] as const;

/**
 * The sanitized shape of a single entry of `state.apps` as seen from inside a
 * worker — see {@link PUBLIC_APP_STATE_KEYS}.
 */
export type PublicAppState = Pick<
	AppState,
	(typeof PUBLIC_APP_STATE_KEYS)[number]
>;

export type NubeSDKWorkerState = NubeSDKState & {
	apps?: Record<AppID, PublicAppState>;
};

export const WORKER_PRIVATE_STATE_PATHS = ["ui.slots", "config"];
